// @ts-nocheck

import { filter, sample, some, every, reduce } from 'lodash';
import memoize from 'memoize-state';

import { common as commonAssets, optionnal as optionnalAssets, banners, badges } from '../../../assets/img/betshare';
import { SportIconsBySportId } from '../../../constants/ui/icons';
import { getLang } from '../../../i18n/l10n';
import { makeEntitiesBetslipsEntityStateByBetslipRefSelector } from '../../../selectors/betslip.selector';
import { BetslipEntity } from '../../../states/entities/betslips';
import { BetslipTypes } from '../../../types/history';
import getBetslipNotableCase from '../../../utils/getBetslipNotableCase';
import { getIllustrations } from '../constants/illustrationsPerCase';

import { getEnhancedOutcomes } from './common';

export const getAllAssets = (
  betslipNotableCase,
  status,
  hasBonus,
  hasBetbuilder,
  isSystem,
  winningsAmount,
  promotionCodeId,
  sportIds = [],
) => {
  let finalAssets = { ...commonAssets };

  let layers = null;
  const cases = getIllustrations(betslipNotableCase, getLang());
  if (cases.length === 1) {
    layers = cases[0];
  } else {
    // if multiple choice for a case, select randomly
    layers = sample(cases);
  }

  // check if illustration has one or two layers
  let mainIllustration = null;
  let topLayer = null;
  if (typeof layers === 'string') {
    mainIllustration = layers;
  } else {
    mainIllustration = layers[0];
    topLayer = layers[1];
  }
  finalAssets = { ...finalAssets, mainIllustration };
  if (topLayer) {
    finalAssets = { ...finalAssets, topLayer };
  }

  // main area banners
  const lang = getLang().toUpperCase();
  const simpleStatus = BetslipEntity.getDisplayStatus(status, winningsAmount);

  finalAssets = { ...finalAssets, badge: badges[lang][simpleStatus], banner: banners[simpleStatus].banner };
  if (hasBonus) {
    finalAssets = { ...finalAssets, bannerBonus: banners[simpleStatus].bonus };
    const isCombobooster = promotionCodeId && promotionCodeId.indexOf('BOOSTER-PERMANENT') !== -1;
    if (isCombobooster) {
      finalAssets = { ...finalAssets, combobooster: optionnalAssets.combobooster };
    }
  }

  // specific assets
  if (hasBetbuilder) {
    finalAssets = { ...finalAssets, betbuilderLogo: optionnalAssets.betbuilderLogo };
  }

  // sport icons
  sportIds.forEach((sportId) => {
    finalAssets[`sportIcon${sportId}`] = SportIconsBySportId[sportId];
  });

  return finalAssets;
};

export const betshareSelector = memoize((state, { betslipReference }) => {
  const selectBetslip = makeEntitiesBetslipsEntityStateByBetslipRefSelector();
  const betslip = selectBetslip(state, { betslipReference });

  let assets = {};
  let canvasAreas = [];
  let needAskBetslipInfo = true;
  let outcomesDetails = [];

  if (betslip) {
    const {
      status,
      type,
      possibleBonus,
      winningsBonus,
      globalOdds,
      winningsAmount,
      betAmount,
      details,
      promotionCodeId,
    } = betslip;

    needAskBetslipInfo = false;
    outcomesDetails = details;

    const enhancedOutcomes = getEnhancedOutcomes(state, details);
    const isSystem = type === BetslipTypes.SYSTEM;
    const hasBonus = (possibleBonus && possibleBonus > 0) || (winningsBonus && winningsBonus > 0);
    const hasBetbuilder = some(enhancedOutcomes, 'isBetbuilder');
    const allBetbuilder = every(enhancedOutcomes, 'isBetbuilder');
    const lostOutcomes = filter(enhancedOutcomes, { outcomeHasOccured: false });
    const oneLost = lostOutcomes && lostOutcomes.length === 1;
    const allLost = every(enhancedOutcomes, { outcomeHasOccured: false });
    const sportIds = reduce(
      enhancedOutcomes,
      (ids, outcome) => {
        if (outcome.sportId) {
          ids.add(outcome.sportId);
        }
        return ids;
      },
      new Set(),
    );

    const betslipNotableCase = getBetslipNotableCase(
      status,
      type,
      globalOdds,
      betAmount,
      winningsAmount,
      allBetbuilder,
      oneLost,
      allLost,
    );

    assets = getAllAssets(
      betslipNotableCase,
      status,
      hasBonus,
      hasBetbuilder,
      isSystem,
      winningsAmount,
      promotionCodeId,
      sportIds,
    );

    // determine canvas areas
    canvasAreas = ['header', 'mainArea', 'footer', ...details.reduce((acc, cur) => [...acc, cur.outcomeId], [])];
    if (type === BetslipTypes.SYSTEM) {
      canvasAreas.push('systemHeader');
    }
    if (hasBonus) {
      canvasAreas.push('bonus');
    }
  }

  return {
    betslipReference,
    needAskBetslipInfo,
    assets,
    canvasAreas,
    outcomesDetails,
  };
});
